<script setup lang="ts">
/**
 * 投稿者プロフィール画像投稿コンポーネント。
 * APIへリクエスト送付も行い、Storeへ投稿されたプロフィール画像IDを保持する。
 * バリデーションエラー表示にも対応。
 */

import { defineProps } from "vue";
import MediaUtil from "@/modules/utils/mediaUtil";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import { ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { PropType, reactive, toRefs, inject } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import MediaController from "@/controllers/communityTalk/mediaController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CustomLabel from "./CustomLabel.vue";

const mediaController = new MediaController();

interface State {
	/**
	 * inputHTMLの内容。
	 */
	inputElement: HTMLInputElement | null;
	/**
	 * 画像投稿APIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * 画像投稿APIエラーメッセージ。
	 */
	errorMessage: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 親コンポーネントからのAPIエラーメッセージ。
	 */
	parentErrorMessage: string;
}
// reactiveな変数宣言
const state = reactive<State>({
	inputElement: null,
	status: ApiPostLoadStatus.beforeRequest,
	errorMessage: "",
	isProcessing: false,
	parentErrorMessage: "",
});
const { inputElement, status, errorMessage, isProcessing, parentErrorMessage } = toRefs(state);

const props = defineProps({
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
		default: "",
	},
	/**
	 * 投稿種類。
	 * トークルームとしてのニックネーム: `TalkRoom`
	 * リプライとしてのニックネーム: `Reply`
	 */
	postType: {
		type: String,
		required: true,
		default: "TalkRoom",
		validator(val: string) {
			return ["TalkRoom", "Reply"].includes(val);
		},
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 親コンポーネントからのAPIエラーオブジェクト。
	 */
	apiValidationErrorViewModel: {
		type: Object as PropType<ApiValidationErrorViewModel | null>,
		required: true,
		default: null,
	},
});

const name = props.postType === "TalkRoom" ? "ProfilePicId" : "reply.ProfilePicId";

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// バリデーションエラーメッセージの表示(親コンポーネントからの)
if (props.apiValidationErrorViewModel) {
	const targetValidationErrorViewModel = props.apiValidationErrorViewModel.details.filter((e) => e.target === name);
	if (targetValidationErrorViewModel.length > 0) {
		const validationErrorMessages = targetValidationErrorViewModel.map((e) => e.message);
		state.errorMessage = validationErrorMessages.join(", ");
	}
}

// ファイル選択ボタン押下時の処理
const onClickInputFileButton = (): void => {
	if (state.inputElement) {
		state.inputElement.click();
	}
};

// 画像をアップロード
const picture = async (): Promise<void> => {
	// 画像ファイルが存在しなければ処理を空振り
	if (!state.inputElement) return;
	if (!state.inputElement.files) return;

	state.isProcessing = true;
	state.status = ApiPostLoadStatus.loading;

	try {
		const image = state.inputElement.files[0];

		// 画像ファイルのバリデーションチェック
		const validErrorMessages = mediaController.checkProfilePicture(image);
		if (validErrorMessages.length !== 0) {
			state.errorMessage = validErrorMessages.join(",");
			// inputタグ内部のファイルデータを削除
			state.inputElement.value = "";
			return;
		}

		// APIリクエスト
		const response = await mediaController.createProfilePicture(image, communityTalkStore.getTenantId());

		// Storeへプロフィール画像データを保持
		communityTalkStore.setCommunityTalkUserProfilePicViewModel({
			profilePicIdTmp: response.tmpId,
			profilePicUrl: response.url,
		});
		state.errorMessage = "";
	} catch (e) {
		const otherErrorMessage = "画像のアップロードに失敗しました。";
		// エラー内容がundefinedの場合、一律同一エラー表示
		if (!e) {
			state.errorMessage = otherErrorMessage;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((e as any).details.filter((x: any) => x.target === "ImageFile")[0].code === "ValidationErrorDetail") {
			// バリデーションエラーであれば内容を表示
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			state.errorMessage = (e as any).details.filter((x: any) => x.target === "ImageFile")[0].message;
		} else {
			// それ以外のエラーであれば、一律同一エラー表示
			state.errorMessage = otherErrorMessage;
		}
	} finally {
		// 成功しても失敗しても、再度画像を投稿するようにしたいのでAPIステータスは投稿前のステータスへ
		state.status = ApiPostLoadStatus.beforeRequest;
		state.isProcessing = false;
	}
};

// 画像の登録解除
const detachImage = (): void => {
	// inputタグ内部のファイルデータを削除
	if (!state.inputElement) return;
	state.inputElement.value = "";

	// Storeへプロフィール画像データを保持
	communityTalkStore.setCommunityTalkUserProfilePicViewModel({
		profilePicIdTmp: "",
		profilePicUrl: "",
	});
};
</script>

<template>
	<div class="revico-community-talk-post-profile-picture">
		<api-post-load-master :status="status">
			<template #beforeRequest>
				<custom-label class="revico-community-talk-post-profile-picture--label" text="プロフィール画像" :disabled="disabled" />
				<div class="revico-community-talk-post-profile-picture-wrapper">
					<button type="button" class="revico-community-talk-post-profile-picture--input-button" :disabled="disabled || isProcessing" @click="onClickInputFileButton">ファイルを選択</button>
					<input
						ref="inputElement"
						@change="picture"
						type="file"
						accept="image/gif, image/png, image/jpeg"
						:name="name"
						:disabled="disabled || isProcessing"
						class="revico-community-talk-post-profile-picture--input"
					/>

					<!-- 画像投稿時の注意文言 -->
					<ul class="revico-community-talk-post-profile-picture-description">
						<li class="revico-community-talk-post-profile-picture-description--item">アップロードできる画像拡張子はpng/jpg/jpeg/gif(静止画)です</li>
						<li class="revico-community-talk-post-profile-picture-description--item">画像サイズの上限は{{ MediaUtil.maxFileSize.image / 1024 / 1024 }}MBです。</li>
					</ul>

					<!-- 画像のプレビュー -->
					<p class="revico-community-talk-post-profile-picture-preview" v-if="communityTalkStore.getCommunityTalkUserProfilePicViewModel().profilePicUrl">
						<img class="revico-community-talk-post-profile-picture-preview--img" :src="communityTalkStore.getCommunityTalkUserProfilePicViewModel().profilePicUrl" @error="detachImage" />
						<!-- プレビュー解除ボタン -->
						<button class="revico-community-talk-post-profile-picture-preview--detach-button" type="button" @click="detachImage">選択を解除</button>
					</p>

					<!-- 投稿画像APIからのAPIエラーメッセージ -->
					<div class="revico-community-talk-post-profile-picture-error-message" v-if="errorMessage">
						{{ errorMessage }}
					</div>
					<!-- 親コンポーネントからのAPIエラーメッセージ -->
					<div class="revico-community-talk-post-profile-picture-error-message" v-if="parentErrorMessage">
						{{ parentErrorMessage }}
					</div>
				</div>
			</template>
		</api-post-load-master>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk .revico-community-talk {
	&-post-profile-picture {
		&--label {
			margin-top: 16px;
		}
		&-wrapper {
			padding: 12px;
			background-color: #fff;
		}

		&--input {
			display: none;
		}
		&--input-button {
			@include communitytalk_input(#fff);
			padding: 6px;
			background-color: var(--base-bg-color);
			cursor: pointer;
			resize: none;
			@include disabledInput;
		}

		&-description {
			width: 100%;
			margin-top: 6px;
			font-size: 8px;
			&--item::before {
				content: "※";
			}
		}

		&-preview {
			width: 100%;
			margin-top: 12px;
			background-color: #fff;
			text-align: center;
			&--img {
				width: 40px;
				height: 40px;
				border-radius: 50%;
				object-fit: cover;
				transition: 0.1s;
			}
			&--detach-button {
				display: inline-block;
				width: 100%;
				margin-top: 8px;
				padding: 4px;
				color: var(--base-font-color);
				font-size: 12px;
				background-color: var(--base-bg-color);
				border: none;
				cursor: pointer;
			}
		}

		&-error-message {
			margin-top: 12px;
			color: var(--base-error-message-font-color);
			font-size: 0.9em;
		}
	}
}
</style>
